// ─── ACCÈS : e-mails autorisés + administrateurs / commerce ───────
// Deux listes qui commandent l'entrée dans la suite :
//   • config/inscription.emails — qui a le droit de créer un compte
//   • config/admin.emails / .commerce — qui administre (source des claims)
// Principes app-wide : ＋ dans l'EN-TÊTE de section (jamais de gros bouton),
// champ de saisie = RECHERCHE, suppression au SWIPE.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import {
  PageHeader, SectionPliable, LigneSwipe, Bascule, stylesForm,
  useUI, useT, useAuth, HIT, confirmerSuppression,
} from '@mop/core';
import {
  ecouterEmailsAutorises, ajouterEmailAutorise, basculerEmailAutorise, supprimerEmailAutorise,
  ecouterConfigAdmin, retirerRoleParEmail, ajouterRoleParEmail,
  ecouterModeInscription, setApprobationAuto,
  ecouterEmailsInterdits, autoriserEmail, interdireEmail, retirerInterdit,
  approuverCompte, rejeterCompte,
} from './services/comptes';
import { ecouterComptes } from './services/admin';
import FeuilleChoixCompte from './FeuilleChoixCompte';
import LigneCompte from './LigneCompte';

export default function EcranAcces() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user, estAdmin } = useAuth();
  const [emails, setEmails] = useState([]);
  const [interdits, setInterdits] = useState([]);
  const [modeAuto, setModeAuto] = useState(false);
  const [cfg, setCfg] = useState({ emails: [], commerce: [] });
  const [comptes, setComptes] = useState([]);     // pour afficher le nom des adresses
  const [q, setQ] = useState('');                 // recherche dans la liste
  const [ouvert, setOuvert] = useState('inscription');
  const [choix, setChoix] = useState(null);       // 'inscription' | 'admin' | 'commerce'
  const [err, setErr] = useState('');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => ecouterEmailsAutorises(setEmails), []);
  useEffect(() => ecouterEmailsInterdits(setInterdits), []);
  useEffect(() => ecouterModeInscription(setModeAuto), []);
  useEffect(() => ecouterConfigAdmin(setCfg), []);
  useEffect(() => ecouterComptes(setComptes, () => {}), []);
  // Rafraîchit le texte « en attente depuis... » sans re-souscrire aux comptes.
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);

  // Demandes d'inscription en attente d'approbation, plus anciennes d'abord.
  const pending = useMemo(() => (comptes || [])
    .filter(c => c.statut === 'en_attente')
    .sort((a, b) => new Date(a.creeLe || 0) - new Date(b.creeLe || 0)), [comptes]);

  // Durée d'attente lisible depuis une date ISO.
  const attente = (iso) => {
    if (!iso) return '';
    const min = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
    if (min < 60) return t('admin.attMin', { n: min });
    const h = Math.floor(min / 60);
    if (h < 24) return t('admin.attHeures', { n: h });
    return t('admin.attJours', { n: Math.floor(h / 24) });
  };

  // Ces listes ne stockent que des adresses : on retrouve le COMPTE par
  // l'e-mail. On renvoie le compte entier, pas seulement son prénom : c'est son
  // EXISTENCE qui dit « inscrit », pas la présence d'un nom (cf. LigneCompte).
  const compteDe = useMemo(() => {
    const m = new Map(comptes.filter(c => c.email).map(c => [c.email.toLowerCase(), c]));
    return (email) => m.get(String(email || '').toLowerCase()) || null;
  }, [comptes]);
  const nomDe = (email) => compteDe(email)?.prenom || '';

  // La recherche porte sur l'adresse ET sur le nom affiché.
  const visibles = useMemo(() => {
    const rq = q.trim().toLowerCase();
    if (!rq) return emails;
    return emails.filter(e => `${e.email || ''} ${nomDe(e.email)}`.toLowerCase().includes(rq));
  }, [emails, q, compteDe]);   // eslint-disable-line react-hooks/exhaustive-deps

  const agir = async (fn) => { try { await fn(); } catch (e) { setErr(e?.message || String(e)); } };
  // Choix d'un compte → ajoute son e-mail à la liste visée.
  const choisirCompte = (c) => agir(() => (choix === 'inscription'
    ? ajouterEmailAutorise(c.email)
    : ajouterRoleParEmail(c.email, choix, user?.email)));
  const retirerRole = (email, type) => confirmerSuppression(
    t(type === 'admin' ? 'admin.retirerAdmin' : 'admin.retirerCommerce'),
    t('admin.retirerRoleMsg', { email }),
    () => agir(() => retirerRoleParEmail(email, type, user?.email)));

  // Demande en attente → Autorisé (approuve aussi le compte) ou Interdit (rejette le compte).
  const autoriserAttente = (u) => agir(() => Promise.all([autoriserEmail(u.email), approuverCompte(u.uid)]));
  const interdireAttente = (u) => confirmerSuppression(
    t('admin.interdireTitre'), t('admin.interdireMsg', { email: u.email }),
    () => agir(() => Promise.all([interdireEmail(u.email), rejeterCompte(u.uid)])));
  const debloquerInterdit = (email) => agir(() => retirerInterdit(email));

  const plus = (cible) => (
    <TouchableOpacity hitSlop={HIT} onPress={() => { setOuvert(cible === 'inscription' ? 'inscription' : 'admins'); setChoix(cible); }}
      accessibilityLabel={t('commun.ajouter')}>
      <Text style={s.plus}>＋</Text>
    </TouchableOpacity>
  );
  // Le swipe n'existe pas sur le web (cf. LigneSwipe) : l'appui long prend le
  // relais pour que la suppression reste possible partout.
  const ligneRole = (email, type) => (
    <LigneSwipe key={`${type}:${email}`} espacement={6} enabled={estAdmin}
      onSupprimer={estAdmin ? () => retirerRole(email, type) : undefined}>
      <TouchableOpacity style={s.ligne} activeOpacity={0.7} disabled={!estAdmin}
        onLongPress={() => retirerRole(email, type)}>
        <LigneCompte email={email} nom={nomDe(email)} inscrit={!!compteDe(email)} />
      </TouchableOpacity>
    </LigneSwipe>
  );

  return (
    <View style={s.page}>
      <PageHeader titre={t('admin.acces')} iconeTitre="🔑" />
      <ScrollView contentContainerStyle={s.corps}>
        {!!err && <Text style={s.err}>⚠️ {err}</Text>}

        {/* Qui peut créer un compte. */}
        <SectionPliable titre={t('admin.emailsAutorises')} emoji="✉️"
          sousTitre={t('admin.nEmails', { n: emails.length })} action={plus('inscription')}
          ouvert={ouvert === 'inscription'} onToggle={() => setOuvert(o => (o === 'inscription' ? null : 'inscription'))}>
          <Text style={s.aide}>{t('admin.emailsAutorisesAide')}</Text>

          <View style={s.switchRow}>
            <View style={{ flex: 1 }}>
              <Text style={s.groupe}>{t('admin.modeInscrTitre')}</Text>
              <Text style={s.switchTxt}>{modeAuto ? t('admin.modeAutoDesc') : t('admin.modeManuelDesc')}</Text>
            </View>
            <Bascule valeur={modeAuto} accessibilityLabel={t('admin.modeInscrTitre')}
              onChange={(v) => agir(() => setApprobationAuto(v))} />
          </View>

          {/* Le champ FILTRE la liste (l'ajout se fait par le ＋ de l'en-tête). */}
          <View style={s.barre}>
            <Text style={s.ic}>🔍</Text>
            <TextInput style={s.rech} value={q} onChangeText={setQ} autoCapitalize="none" autoCorrect={false}
              placeholder={t('admin.rechercheEmail')} placeholderTextColor={UI.faint} />
            {!!q && <TouchableOpacity onPress={() => setQ('')}><Text style={s.ic}>✕</Text></TouchableOpacity>}
          </View>
          {visibles.length === 0 ? <Text style={s.vide}>{t('admin.aucunEmail')}</Text> : visibles.map(e => (
            <LigneSwipe key={e.email} espacement={6}
              onSupprimer={() => agir(() => supprimerEmailAutorise(e.email))}>
              <View style={s.ligne}>
                <TouchableOpacity style={{ flex: 1 }} activeOpacity={0.7}
                  onLongPress={() => confirmerSuppression(t('admin.emailsAutorises'),
                    t('admin.retirerRoleMsg', { email: e.email }),
                    () => agir(() => supprimerEmailAutorise(e.email)))}>
                  <LigneCompte email={e.email} nom={nomDe(e.email)} inscrit={!!compteDe(e.email)} />
                </TouchableOpacity>
                <Bascule valeur={e.actif !== false} accessibilityLabel={e.email}
                  onChange={(v) => agir(() => basculerEmailAutorise(e.email, v))} />
              </View>
            </LigneSwipe>
          ))}
        </SectionPliable>

        {/* Demandes d'inscription à approuver ou refuser. */}
        <SectionPliable titre={t('admin.tabAttente')} emoji="⏳"
          sousTitre={t('admin.nAttente', { n: pending.length })}
          ouvert={ouvert === 'attente'} onToggle={() => setOuvert(o => (o === 'attente' ? null : 'attente'))}>
          {pending.length === 0 ? <Text style={s.vide}>{t('admin.aucuneAttente')}</Text> : pending.map(u => (
            <View key={u.uid} style={s.ligne}>
              <View style={{ flex: 1 }}>
                <LigneCompte email={u.email} nom={u.prenom} inscrit />
                <Text style={s.attenteTxt}>{attente(u.creeLe)}</Text>
              </View>
              <View style={s.attenteActions}>
                <TouchableOpacity hitSlop={HIT} onPress={() => autoriserAttente(u)}>
                  <Text style={s.btnOk}>✅ {t('admin.autoriser')}</Text>
                </TouchableOpacity>
                <TouchableOpacity hitSlop={HIT} onPress={() => interdireAttente(u)}>
                  <Text style={s.btnKo}>🚫 {t('admin.interdire')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </SectionPliable>

        {/* Liste noire : adresses définitivement bloquées. */}
        <SectionPliable titre={t('admin.tabInterdits')} emoji="🚫"
          sousTitre={t('admin.nInterdits', { n: interdits.length })}
          ouvert={ouvert === 'interdits'} onToggle={() => setOuvert(o => (o === 'interdits' ? null : 'interdits'))}>
          {interdits.length === 0 ? <Text style={s.vide}>{t('admin.aucunInterdit')}</Text> : interdits.map(e => (
            <View key={e.email} style={s.ligne}>
              <View style={{ flex: 1 }}>
                <Text style={s.ligneTxt} numberOfLines={1}>{e.email}</Text>
                {!!e.ajouteLe && (
                  <Text style={s.attenteTxt}>{t('admin.interditLe', { date: new Date(e.ajouteLe).toLocaleDateString('fr-FR') })}</Text>
                )}
              </View>
              <TouchableOpacity hitSlop={HIT} onPress={() => debloquerInterdit(e.email)}>
                <Text style={s.btnOk}>{t('admin.debloquer')}</Text>
              </TouchableOpacity>
            </View>
          ))}
        </SectionPliable>

        {/* Qui administre (source des custom claims). */}
        <SectionPliable titre={t('admin.administrateurs')} emoji="🛡️"
          sousTitre={t('admin.nAdmins', { n: cfg.emails.length, c: cfg.commerce.length })}
          ouvert={ouvert === 'admins'} onToggle={() => setOuvert(o => (o === 'admins' ? null : 'admins'))}>
          <Text style={s.aide}>{t('admin.administrateursAide')}</Text>

          <View style={s.groupeTete}>
            <Text style={s.groupe}>🛡️ {t('admin.role_admin')}</Text>
            {estAdmin && plus('admin')}
          </View>
          {cfg.emails.map(e => ligneRole(e, 'admin'))}

          <View style={s.groupeTete}>
            <Text style={s.groupe}>💼 {t('admin.role_commerce')}</Text>
            {estAdmin && plus('commerce')}
          </View>
          {cfg.commerce.length === 0
            ? <Text style={s.vide}>{t('admin.aucunCommerce')}</Text>
            : cfg.commerce.map(e => ligneRole(e, 'commerce'))}
          <Text style={s.aide}>{t('admin.swipeRetirer')}</Text>
        </SectionPliable>
      </ScrollView>

      {/* Sélecteur de compte (nom + adresse, avec recherche). */}
      <FeuilleChoixCompte visible={!!choix} onFermer={() => setChoix(null)} onChoisir={choisirCompte}
        titre={t(choix === 'inscription' ? 'admin.autoriserCompte' : 'admin.choisirCompte')}
        exclus={choix === 'inscription' ? emails.map(e => e.email)
          : (choix === 'admin' ? cfg.emails : cfg.commerce)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  page: { flex: 1, backgroundColor: UI.bg },
  corps: { padding: 12, paddingBottom: 30 },
  barre: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.champ,
           borderRadius: 11, paddingHorizontal: 12, borderWidth: 0.5, borderColor: UI.champBord, marginBottom: 4 },
  ic: { fontSize: 15, color: UI.muted },
  rech: { flex: 1, fontSize: 14.5, color: UI.ink, paddingVertical: 9 },
  plus: { fontSize: 20, color: UI.accent, fontWeight: '700' },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10,
           borderTopWidth: 0.5, borderTopColor: UI.border },
  groupeTete: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 },
  groupe: { fontSize: 12, fontWeight: '900', color: UI.muted, textTransform: 'uppercase', letterSpacing: 0.5 },
  vide: { fontSize: 12.5, color: UI.faint, fontStyle: 'italic', paddingVertical: 8 },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginBottom: 8 },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 8 },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  switchTxt: { fontSize: 11.5, color: UI.faint, lineHeight: 15, marginTop: 1 },
  ligneTxt: { fontSize: 14, fontWeight: '700', color: UI.ink },
  attenteTxt: { fontSize: 11.5, color: UI.faint, marginTop: 2 },
  attenteActions: { flexDirection: 'row', gap: 14 },
  btnOk: { fontSize: 12.5, fontWeight: '700', color: '#16A34A' },
  btnKo: { fontSize: 12.5, fontWeight: '700', color: UI.danger },
});
